import "../inputs.scss";
import "react-draft-wysiwyg/dist/react-draft-wysiwyg.css"; // Have to import to ensure it's correctly included
import "./htmleditor.scss";
import { type FC, useEffect, useRef } from "react";
import * as React from "react";
import type { EditorProps } from "react-draft-wysiwyg";
import * as draftEditor from "react-draft-wysiwyg";
import draftjs from "draft-js";
import draftToHtml from "draftjs-to-html";
import htmlToDraft from "html-to-draftjs";
import { type Override } from "../../../../types/internal/utility";
import ImageModal from "./ImageModal";

type Props = Override<EditorProps, {
    value: draftjs.EditorState | undefined;
    onChange: (editorState: draftjs.EditorState) => void;
    html?: string;
    autoFocus?: boolean;
    className?: string;
}>;

/**
 * Convert HTML string to Draft.js editor
 * @param html
 */
export function convertHTMLToDraft(html: string): draftjs.EditorState {
    // Looks like there is a bug in React-Draft-WYSIWYG
    // https://github.com/jpuri/react-draft-wysiwyg/issues/609
    html = html.trim().replace(/\n|(<p><\/p>)/g, "");

    if(html.length && html.slice(0, 3) !== "<p>" && html.includes("<img")) {
        html = `<p>${html}</p>`;
    }

    const contentBlock = htmlToDraft(html, (nodeName, node) => {
        if(nodeName === "div" && node.children.length === 1) {
            const child = node.children[0] as HTMLImageElement;

            if(child.nodeName.toLowerCase() === "img") {
                return {
                    type      : "IMAGE",
                    mutability: "MUTABLE",
                    data      : {
                        alignment: node.style.textAlign,
                        src      : child.src,
                        alt      : child.alt,
                        height   : child.style.height,
                        width    : child.style.width,
                    },
                };
            }
        }

        return undefined;
    });

    const contentState = draftjs.ContentState.createFromBlockArray(
        contentBlock.contentBlocks,
        contentBlock.entityMap
    );

    return draftjs.EditorState.createWithContent(contentState);
}

/**
 * Convert Draft.js to HTML string
 * @param content
 */
export function convertDraftToHTML(
    content: draftjs.ContentState | draftjs.EditorState
): ReturnType<typeof draftToHtml> {
    if(typeof (content as draftjs.EditorState).getCurrentContent === "function") {
        content = (content as draftjs.EditorState).getCurrentContent();
    }

    return draftToHtml(draftjs.convertToRaw(content as draftjs.ContentState));
}

// Imported like this because react-draft-wysiwyg doesn't export correctly
const Editor = (draftEditor as any).default?.Editor ?? draftEditor.Editor;

// Note: Issues with dropdown options from the toolbar may be caused by StrictMode
// https://github.com/jpuri/react-draft-wysiwyg/issues/1291#issuecomment-1203348769
export const HTMLEditor: FC<Props> = ({
    autoFocus,
    value,
    onChange,
    html,
    className,
    ...props
}) => {
    const editorRef = useRef<draftEditor.Editor>(null);

    const classes = [ "html-editor" ];

    if(typeof className === "string") {
        classes.push(className);
    }

    useEffect(() => {
        if(!html) {
            return;
        }

        onChange(convertHTMLToDraft(html));
        // Note: onChange is intentionally not added
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [ html ]);

    useEffect(() => {
        if(!autoFocus || !editorRef.current) {
            return;
        }

        editorRef.current.focusEditor();
        // Note: autoFocus is intentionally not added
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
        <div className={classes.join(" ")}>
            <Editor
                editorState={value}
                onEditorStateChange={onChange}
                toolbar={{
                    image  : { component: ImageModal },
                    options: [
                        "inline",
                        "blockType",
                        "fontSize",
                        "list",
                        "textAlign",
                        "colorPicker",
                        "link",
                        "image",
                        "history",
                    ],
                    blockType: {
                        inDropdown       : true,
                        options          : [ "Normal", "H1", "H2", "H3", "H4", "H5", "H6", "Blockquote" ],
                        className        : undefined,
                        component        : undefined,
                        dropdownClassName: undefined,
                    },
                }}
                ref={editorRef}
                {...props}
            />
        </div>
    );
};
